/**
 * Bot Management — per-domain L7 DDoS countermeasures + bot challenge config for
 * the Security tab. Pick a domain, then toggle the bot rules (search-engine
 * bypass, scanner block, bad-bot block), the browser integrity check, and the
 * security-level challenge (level / passage / mode). Each "eye" opens the bot
 * catalog (the user-agent tokens a rule matches).
 *
 * Backed by `/waf/ddos` (DDoSService get/update) and `/waf/bots` (bot catalog).
 * Nested `browser_check` / `security` are patched as whole objects (merge the
 * changed field with the current values).
 */
import { useState } from 'react';
import { Bug, Eye } from 'lucide-react';
import { Container, PageHeader } from '@vnetwork/ui/layout';
import { EmptyState } from '@vnetwork/ui/data';
import {
  Skeleton,
  Switch,
  Select,
  SelectTrigger,
  SelectValue,
  SelectContent,
  SelectItem,
  Card,
  CardHeader,
  CardTitle,
  CardBody,
  IconButton,
  Tooltip,
  TooltipProvider,
} from '@vnetwork/ui/primitives';
import { Alert, useToast } from '@vnetwork/ui/feedback';
import {
  useWafDDoSSettings,
  useUpdateWafDDoSSettings,
  wafExtractError,
  type WafDDoSSettings,
  type WafDDoSSettingsUpdate,
} from '@/api/waf';
import { trackFeatureAction } from '@/lib/analytics-events';
import { WafScopeBar, type WafScope } from '../analytics/WafScopeBar';
import { BotCatalogDialog } from './BotCatalogDialog';
import styles from './WafBotManagement.module.css';

const BROWSER_MODE_OPTIONS = [
  { value: 'standard', label: 'Standard' },
  { value: 'strict', label: 'Strict' },
];

const SECURITY_LEVEL_OPTIONS = [
  { value: 'underattack', label: "I'm under attack" },
  { value: 'high', label: 'High' },
  { value: 'medium', label: 'Medium' },
  { value: 'low', label: 'Low' },
  { value: 'essentiallyoff', label: 'Essentially off' },
];

const CHALLENGE_MODE_OPTIONS = [
  { value: 'javascript', label: 'Browser-based (silent)' },
  { value: 'javascript-nodelay', label: 'Browser-based (no delay)' },
  { value: 'human', label: 'Interactive' },
];

/** Challenge passage (security.ttl) — seconds. Adjust once confirmed with vendor. */
const TTL_OPTIONS = [
  { value: 300, label: '5 Minutes' },
  { value: 900, label: '15 Minutes' },
  { value: 1800, label: '30 Minutes' },
  { value: 3600, label: '1 Hour' },
  { value: 14400, label: '4 Hours' },
  { value: 28800, label: '8 Hours' },
  { value: 86400, label: '24 Hours' },
];

function fieldLabel(text: string) {
  return (
    <span style={{ color: 'var(--vn-fg-muted)', fontSize: 'var(--vn-font-size-sm)' }}>{text}</span>
  );
}

/** A label/description row with a trailing Switch (and optional eye → catalog). */
function ToggleRow({
  label,
  description,
  checked,
  pending,
  onChange,
  onEye,
}: {
  label: string;
  description?: string;
  checked: boolean;
  pending: boolean;
  onChange: (v: boolean) => void;
  onEye?: () => void;
}) {
  return (
    <div className={styles.toggleRow}>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 4, minWidth: 0 }}>
        <span className={styles.toggleLabel}>
          <span style={{ fontWeight: 500 }}>{label}</span>
          {onEye && (
            <TooltipProvider>
              <Tooltip content="View matched bots" side="top">
                <IconButton variant="ghost" size="sm" aria-label={`View bots for ${label}`} onClick={onEye}>
                  <Eye size={15} />
                </IconButton>
              </Tooltip>
            </TooltipProvider>
          )}
        </span>
        {description ? (
          <span style={{ color: 'var(--vn-fg-subtle)', fontSize: 'var(--vn-font-size-sm)' }}>{description}</span>
        ) : null}
      </div>
      <Switch checked={checked} aria-label={label} disabled={pending} onCheckedChange={onChange} />
    </div>
  );
}

/** Both settings cards for a resolved domain. */
function SettingsBody({ serviceId, domain }: { serviceId: string; domain: string }) {
  const { show } = useToast();
  const settingsQ = useWafDDoSSettings(serviceId, domain);
  const d = settingsQ.data;
  const [catalog, setCatalog] = useState<{ category: string; title: string } | null>(null);

  const mut = useUpdateWafDDoSSettings(serviceId, domain, {
    onSuccess: () => { show({ tone: 'success', title: 'Bot settings updated' }); trackFeatureAction('waf', 'bot', 'edit'); },
    onError: (e) => show({ tone: 'danger', title: wafExtractError(e, 'Update failed') }),
  });
  const patch = (p: WafDDoSSettingsUpdate) => mut.mutate(p);
  const pending = mut.isPending;

  if (settingsQ.isLoading) return <Skeleton style={{ height: 320 }} />;
  if (settingsQ.isError) {
    return (
      <Alert tone="danger" title="Couldn't load bot settings" actions={<button onClick={() => settingsQ.refetch()}>Retry</button>}>
        {wafExtractError(settingsQ.error, 'The bot-management settings for this domain failed to load.')}
      </Alert>
    );
  }
  if (!d) return null;

  // Helpers that merge a changed field into the whole nested object.
  const setBrowser = (next: Partial<WafDDoSSettings['browser_check']>) =>
    patch({ browser_check: { enable: d.browser_check.enable, mode: d.browser_check.mode, ...next } });
  const setSecurity = (next: Partial<WafDDoSSettings['security']>) =>
    patch({ security: { ...d.security, ...next } });

  const ttlValue = d.security.ttl != null ? String(d.security.ttl) : '';
  const ttlHasValue = TTL_OPTIONS.some((o) => String(o.value) === ttlValue);

  return (
    <div style={{ display: 'grid', gap: 'var(--vn-space-4)' }}>
      {/* Application layer */}
      <Card>
        <CardHeader>
          <CardTitle>Application layer</CardTitle>
        </CardHeader>
        <CardBody>
          <div className={styles.cardSubtitle}>Comprehensive L7 DDoS countermeasures</div>
          <div className={styles.rows}>
            <ToggleRow
              label="Allow search engine crawler bypass security"
              checked={d.seo_enable}
              pending={pending}
              onChange={(v) => patch({ seo_enable: v })}
              onEye={() => setCatalog({ category: 'spiders', title: 'Search-engine crawlers' })}
            />
            <ToggleRow
              label="Block security/vulnerability scanner"
              checked={d.block_security_scan}
              pending={pending}
              onChange={(v) => patch({ block_security_scan: v })}
              onEye={() => setCatalog({ category: 'scan_bot', title: 'Security scanners' })}
            />
            <ToggleRow
              label="Block rogue crawlers and bad bots"
              checked={d.block_bad_bot}
              pending={pending}
              onChange={(v) => patch({ block_bad_bot: v })}
              onEye={() => setCatalog({ category: 'bad_bot', title: 'Bad bots' })}
            />
            <div>
              <ToggleRow
                label="Browser integrity check"
                checked={d.browser_check.enable}
                pending={pending}
                onChange={(v) => setBrowser({ enable: v })}
              />
              <div style={{ marginTop: 'var(--vn-space-2)' }}>
                <Select
                  value={d.browser_check.mode || 'standard'}
                  disabled={pending}
                  onValueChange={(v) => setBrowser({ mode: v })}
                >
                  <SelectTrigger aria-label="Browser check level" className={styles.select}>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {BROWSER_MODE_OPTIONS.map((o) => (
                      <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
          </div>
        </CardBody>
      </Card>

      {/* Bot management */}
      <Card>
        <CardHeader>
          <CardTitle>Bot management</CardTitle>
        </CardHeader>
        <CardBody>
          <div className={styles.cardSubtitle}>
            If the “Firewall rules — challenge” is currently activated, the challenge page may be triggered twice.
          </div>
          <div className={styles.grid2}>
            <div style={{ display: 'grid', gap: 'var(--vn-space-2)' }}>
              <label style={{ display: 'inline-flex', alignItems: 'center', gap: 'var(--vn-space-2)' }}>
                <Switch
                  checked={d.security.enable}
                  aria-label="Security level"
                  disabled={pending}
                  onCheckedChange={(v) => setSecurity({ enable: v })}
                />
                <span style={{ fontWeight: 500 }}>Security level</span>
              </label>
              <Select
                value={d.security.level || 'high'}
                disabled={pending}
                onValueChange={(v) => setSecurity({ level: v })}
              >
                <SelectTrigger aria-label="Security level" className={styles.select}>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {SECURITY_LEVEL_OPTIONS.map((o) => (
                    <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div style={{ display: 'grid', gap: 'var(--vn-space-2)', alignContent: 'start' }}>
              {fieldLabel('Challenge passage')}
              <Select
                value={ttlValue}
                disabled={pending}
                onValueChange={(v) => setSecurity({ ttl: Number(v) })}
              >
                <SelectTrigger aria-label="Challenge passage" className={styles.select}>
                  <SelectValue placeholder="Select duration" />
                </SelectTrigger>
                <SelectContent>
                  {!ttlHasValue && ttlValue ? (
                    <SelectItem value={ttlValue}>{`${d.security.ttl} seconds`}</SelectItem>
                  ) : null}
                  {TTL_OPTIONS.map((o) => (
                    <SelectItem key={o.value} value={String(o.value)}>{o.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>

          <div style={{ display: 'grid', gap: 'var(--vn-space-2)', marginTop: 'var(--vn-space-4)' }}>
            {fieldLabel('Challenge mode')}
            <Select
              value={d.security.mode || 'javascript'}
              disabled={pending}
              onValueChange={(v) => setSecurity({ mode: v })}
            >
              <SelectTrigger aria-label="Challenge mode" className={styles.select}>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {CHALLENGE_MODE_OPTIONS.map((o) => (
                  <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </CardBody>
      </Card>

      {catalog && (
        <BotCatalogDialog
          serviceId={serviceId}
          category={catalog.category}
          title={catalog.title}
          open={!!catalog}
          onOpenChange={(o) => { if (!o) setCatalog(null); }}
        />
      )}
    </div>
  );
}

export function WafBotManagement({ serviceId }: { serviceId: string }) {
  const [scope, setScope] = useState<WafScope>({});
  const domain = scope.subdomain;
  const noSubdomain = !!scope.zone && !domain;

  return (
    <Container size="xl">
      <PageHeader
        icon={<Bug size={22} />}
        title="Bot Management"
        description="Evade unwanted/malicious bots from accessing the selected subdomain. Security level and challenge mode are customizable to your needs."
      />

      <div className={styles.scopeBar}>
        <WafScopeBar serviceId={serviceId} mode="subdomain" onChange={setScope} />
      </div>

      {noSubdomain ? (
        <EmptyState
          icon={<Bug size={20} />}
          title="No proxied subdomains"
          description="This zone has no proxied subdomain to manage bot protection for. Proxy a record first."
        />
      ) : !domain ? (
        <EmptyState
          icon={<Bug size={20} />}
          title="Select a subdomain"
          description="Pick a domain and subdomain above to manage its bot protection."
        />
      ) : (
        <SettingsBody serviceId={serviceId} domain={domain} />
      )}
    </Container>
  );
}

export default WafBotManagement;
